import { useEffect, useMemo, useState } from 'react';

import {
  CloseIcon,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SearchIcon,
  Switch,
  Text,
} from '@tradener/lumen';
import { debounce } from 'lodash';

export interface CounterpartyFiltersProps {
  search: string;
  hasExposure: boolean;
  onSearch: (value: string) => void;
  onToggleHasExposure: (value: boolean) => void;
}

function CounterpartyFilters({ search, hasExposure, onSearch, onToggleHasExposure }: CounterpartyFiltersProps) {
  const [searchValue, setSearchValue] = useState(search);

  const debouncedSearch = useMemo(() => debounce((value: string) => onSearch(value), 400), [onSearch]);

  const handleClear = () => {
    setSearchValue('');
    onSearch('');
  };

  useEffect(() => {
    setSearchValue(search);
  }, [search]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <Flex align="center" justify="space-between" width="full" minH="8">
      <InputGroup key="counterparty-input-group" width="400px" height="8" textStyle="body2-regular">
        <Input
          value={searchValue}
          bgColor="gray.100"
          textStyle="body2-regular"
          variant="filled"
          colorScheme="grey"
          placeholder="Buscar contraparte"
          autoComplete="off"
          overflow="clip"
          _placeholder={{ color: 'gray.400' }}
          size="sm"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          onChange={(event) => {
            const value = event.target.value;

            setSearchValue(value);
            debouncedSearch(value);
          }}
        />
        <InputRightElement height="full">
          <IconButton
            aria-label="clear-search"
            variant="ghost"
            size="sm"
            colorScheme="blackAlpha"
            color="gray.400"
            _hover={{ bgColor: 'transparent' }}
            onClick={handleClear}
          >
            {searchValue ? <CloseIcon boxSize="5" /> : <SearchIcon boxSize="5" />}
          </IconButton>
        </InputRightElement>
      </InputGroup>
      <FormControl gap="3" display="flex" alignItems="center" width="fit-content">
        <Switch id="has-exposure-filter" isChecked={hasExposure} onChange={() => onToggleHasExposure(!hasExposure)} />
        <FormLabel htmlFor="has-exposure-filter" margin="0">
          <Text textStyle="body2-regular">Com exposição</Text>
        </FormLabel>
      </FormControl>
    </Flex>
  );
}

export default CounterpartyFilters;
